import posthog from 'posthog-js';
import { BLOG_ANALYTICS } from '../types/blogPost';

// Initialize PostHog with the API key from environment variables
// IMPORTANT: Never hardcode the API key directly
const initPostHog = () => {
  // Check for various environment variable naming patterns
  const apiKey = 
    import.meta.env.VITE_POSTHOG_API_KEY || 
    import.meta.env.VITE_PUBLIC_POSTHOG_KEY ||
    import.meta.env.POSTHOG_API_KEY || 
    import.meta.env.REACT_APP_POSTHOG_API_KEY || 
    '';
    
  const apiHost = 
    import.meta.env.VITE_POSTHOG_HOST || 
    import.meta.env.VITE_PUBLIC_POSTHOG_HOST ||
    import.meta.env.POSTHOG_HOST || 
    import.meta.env.REACT_APP_POSTHOG_HOST || 
    'https://app.posthog.com';
  
  if (!apiKey) {
    console.warn('PostHog API key is not set. Analytics will not be tracked.');
    return;
  }

  // Initialize PostHog with configuration
  try {
    posthog.init(apiKey, {
      api_host: apiHost,
      capture_pageview: true,
      autocapture: true,
      capture_pageleave: true,
      disable_session_recording: false, // Enable or disable based on your needs
      session_recording: {
        maskAllInputs: true, // For privacy/security
      },
      persistence: "localStorage" as const,
      property_blacklist: ['$current_url', '$initial_referring_domain', 'password'],
      mask_all_text: false,
      mask_all_element_attributes: false,
      advanced_disable_decide: false,
      debug: false, // Disable debug mode to reduce console noise
      loaded: () => {
        // Set up global error tracking once PostHog is loaded
        setupGlobalErrorTracking();
      }
    });
    
    // Check if PostHog is properly initialized
    if (posthog.__loaded) {
      // Send a test event
      posthog.capture('test_initialization', {
        timestamp: new Date().toISOString(),
        environment: import.meta.env.MODE
      });
    }
  } catch (err) {
    console.error('Error initializing PostHog:', err);
  }
};

// Set up global error tracking for uncaught errors
const setupGlobalErrorTracking = () => {
  // Track uncaught JavaScript errors
  window.addEventListener('error', (event) => {
    const errorInfo = {
      error_type: 'javascript_error',
      error_message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error_stack: event.error?.stack,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href,
      error_source: 'window_error_handler'
    };

    posthog.capture('$exception', errorInfo);
  });

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const errorInfo = {
      error_type: 'unhandled_promise_rejection',
      error_message: event.reason?.message || String(event.reason),
      error_stack: event.reason?.stack,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href,
      error_source: 'unhandled_rejection'
    };

    posthog.capture('$exception', errorInfo);
  });

  // Track React console errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Check if this looks like a React error
    const errorString = args.join(' ');
    if (errorString.includes('React') || errorString.includes('component') || errorString.includes('Warning:')) {
      posthog.capture('$exception', {
        error_type: 'react_console_error',
        error_message: errorString,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        url: window.location.href,
        error_source: 'console_error'
      });
    }
    originalConsoleError.apply(console, args);
  };
};

// Call to identify a user with their email as the ID
export const identifyUser = (user: { id: string; email: string; user_metadata?: Record<string, unknown> }) => {
  if (!user?.email) return;
  
  // Identify the user with their email address
  posthog.identify(user.email, {
    email: user.email,
    userId: user.id,
    // Extract useful properties from Supabase user object
    last_sign_in: user.user_metadata?.last_sign_in || null,
    created_at: user.user_metadata?.created_at || null,
    provider: user.user_metadata?.provider || 'email',
    role: user.user_metadata?.role || 'user',
    is_verified: user.user_metadata?.is_verified || false,
    ...user.user_metadata
  });

  // Record that this user was identified (useful for funnel analysis)
  posthog.capture('user_identified', {
    distinct_id: user.email,
    userId: user.id
  });
};

// Setup company group for a user
export const setUserCompany = (companyId: number, companyName: string) => {
  if (!companyId) return;
  
  // Set the company as a group
  posthog.group('company', companyId.toString(), {
    name: companyName || `Company ${companyId}`,
    company_id: companyId,
    last_active: new Date().toISOString()
  });

  // Also record the current company ID as a user property for easier filtering
  posthog.people.set({
    current_company_id: companyId,
    current_company_name: companyName || `Company ${companyId}`
  });
};

// Track user session with Supabase auth
export const trackAuthEvent = (
  eventName: 'sign_in' | 'sign_up' | 'sign_out' | 'password_recovery' | 'token_refresh',
  properties?: Record<string, unknown>
) => {
  posthog.capture(`auth_${eventName}`, {
    timestamp: new Date().toISOString(),
    ...properties
  });
};

// Track booking-related events
export const trackBookingEvent = (
  eventName: 'booking_created' | 'booking_updated' | 'booking_submitted' | 'booking_status_changed',
  bookingId: string | number,
  properties?: Record<string, unknown>
) => {
  posthog.capture(`booking_${eventName}`, {
    booking_id: bookingId,
    timestamp: new Date().toISOString(),
    ...properties
  });
};

// Track product-related events
export const trackProductEvent = (
  eventName: 'product_created' | 'product_updated' | 'product_deleted' | 'product_imported',
  productId?: string | number,
  properties?: Record<string, unknown>
) => {
  posthog.capture(`product_${eventName}`, {
    product_id: productId,
    timestamp: new Date().toISOString(),
    ...properties
  });
};

// Track feature usage
export const trackFeatureUsage = (featureName: string, properties?: Record<string, unknown>) => {
  posthog.capture(`feature_used_${featureName}`, {
    feature: featureName,
    timestamp: new Date().toISOString(),
    ...properties
  });
};

// Track custom events
export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  posthog.capture(eventName, {
    timestamp: new Date().toISOString(),
    ...properties
  });
};

// Track blog post view (actual visitor on single post page)
export const trackBlogPostViewed = (postId: string, postTitle: string) => {
  posthog.capture(BLOG_ANALYTICS.VIEWED_EVENT, {
    [BLOG_ANALYTICS.POST_ID]: postId,
    [BLOG_ANALYTICS.POST_TITLE]: postTitle,
    timestamp: new Date().toISOString(),
  });
};

// Enhanced error tracking with better categorization
export const captureError = (
  error: unknown, 
  context?: Record<string, unknown>,
  errorSource = 'manual'
) => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  
  // Determine error severity based on error type and context
  const errorSeverity = determineErrorSeverity(errorObj, context);
  
  // Create comprehensive error context
  const errorContext = {
    // Core error information
    error_message: errorObj.message,
    error_name: errorObj.name,
    error_stack: errorObj.stack,
    error_source: errorSource,
    error_severity: errorSeverity,
    
    // Environment context
    timestamp: new Date().toISOString(),
    user_agent: navigator.userAgent,
    url: window.location.href,
    pathname: window.location.pathname,
    
    // Browser context
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    
    // Performance context (if available)
    memory_used: (performance as unknown as { memory?: { usedJSHeapSize?: number } })?.memory?.usedJSHeapSize,
    memory_total: (performance as unknown as { memory?: { totalJSHeapSize?: number } })?.memory?.totalJSHeapSize,
    
    // Network context
    connection_type: (navigator as unknown as { connection?: { effectiveType?: string } })?.connection?.effectiveType,
    connection_downlink: (navigator as unknown as { connection?: { downlink?: number } })?.connection?.downlink,
    
    // Application context
    environment: import.meta.env.MODE,
    
    // Custom context from caller
    ...context
  };

  // Use PostHog's standard exception event for better error tracking dashboard
  posthog.capture('$exception', errorContext);

  // Also send as custom error event for additional filtering
  posthog.capture('app_error', errorContext);

  // Log to console with appropriate level based on severity
  if (import.meta.env.DEV) {
    const logLevel = errorSeverity === 'critical' ? 'error' : 
                    errorSeverity === 'high' ? 'error' : 
                    errorSeverity === 'medium' ? 'warn' : 'log';
    
    console[logLevel](`[${errorSeverity.toUpperCase()}] Error captured for PostHog:`, errorObj, context);
  }
};

// Helper function to determine error severity
const determineErrorSeverity = (
  error: Error, 
  context?: Record<string, unknown>
): 'low' | 'medium' | 'high' | 'critical' => {
  // Critical errors - these break core functionality
  if (
    error.name === 'ChunkLoadError' ||
    error.message.includes('ChunkLoadError') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Network Error') ||
    error.message.includes('Failed to fetch') ||
    context?.source === 'payment' ||
    context?.source === 'auth' ||
    context?.action === 'booking_submission'
  ) {
    return 'critical';
  }
  
  // High severity - important features affected
  if (
    error.name === 'TypeError' ||
    error.name === 'ReferenceError' ||
    error.message.includes('Cannot read propert') ||
    error.message.includes('is not defined') ||
    context?.source === 'booking' ||
    context?.source === 'invoice' ||
    context?.source === 'pricing'
  ) {
    return 'high';
  }
  
  // Medium severity - non-critical features
  if (
    error.name === 'ValidationError' ||
    error.message.includes('validation') ||
    error.message.includes('required') ||
    context?.source === 'form' ||
    context?.source === 'ui'
  ) {
    return 'medium';
  }
  
  // Default to low severity
  return 'low';
};

// Super properties that will be sent with every event
export const setSuperProperties = (properties: Record<string, unknown>) => {
  posthog.register(properties);
};

// Reset user - use when logging out
export const resetUser = () => {
  posthog.reset();
};

// Specific error tracking functions for different scenarios
export const captureApiError = (
  endpoint: string,
  method: string,
  statusCode: number,
  error: unknown,
  context?: Record<string, unknown>
) => {
  captureError(error, {
    source: 'api',
    endpoint,
    method,
    status_code: statusCode,
    error_category: 'network',
    ...context
  }, 'api_call');
};

export const captureValidationError = (
  formName: string,
  field: string,
  error: unknown,
  context?: Record<string, unknown>
) => {
  captureError(error, {
    source: 'validation',
    form_name: formName,
    field_name: field,
    error_category: 'user_input',
    ...context
  }, 'form_validation');
};

export const capturePaymentError = (
  paymentMethod: string,
  error: unknown,
  context?: Record<string, unknown>
) => {
  captureError(error, {
    source: 'payment',
    payment_method: paymentMethod,
    error_category: 'payment',
    ...context
  }, 'payment_processing');
};

export const captureBookingError = (
  bookingId: string | number,
  action: string,
  error: unknown,
  context?: Record<string, unknown>
) => {
  captureError(error, {
    source: 'booking',
    booking_id: bookingId,
    action,
    error_category: 'business_logic',
    ...context
  }, 'booking_operation');
};

// Function to manually trigger error tracking setup (useful for testing)
export const initErrorTracking = () => {
  if (typeof window !== 'undefined' && posthog.__loaded) {
    setupGlobalErrorTracking();
  }
};

export { posthog, initPostHog }; 